import type { FC } from 'react'
import { useCallback, useEffect, useMemo, useState } from 'react'

import { Select } from 'antd'
import classNames from 'classnames'
import { addMinutes, format, isBefore, isEqual, isValid } from 'date-fns'

import { TIME_FORMAT } from 'common/constants/timeFormat'
import { RegexService } from 'common/services/regexService'

import { INFO_CONSTANTS } from 'features/Home/constants/infoConstants'

import styles from './TimePickerBySlots.module.scss'

interface IProps {
  index?: string
  options: { start_time: Date; end_time: Date }[]
  date: string
  value?: Date
  onUpdateField: (value: Date | null) => void
  onFieldChange?: (index: string, value: string) => void
  onFocus?: () => void
  onHide?: () => void
  setFieldError?: Function
  fieldName?: string
  placeholder?: string
  disabled?: boolean
}

export const SingleSlotTimePicker: FC<IProps> = ({
  options,
  value,
  onUpdateField,
  setFieldError,
  fieldName,
  onFocus,
  onHide,
  placeholder = 'Select time',
  ...rest
}) => {
  const [selectedTime, setSelectedTime] = useState<string | null>(null)
  const [customTime, setCustomTime] = useState<string | null>(null)
  const [isOpen, setIsOpen] = useState(false)
  const { disabled } = rest

  useEffect(() => {
    const handleScroll = () => {
      setIsOpen(false)
    }

    const modal = document.querySelector('.ant-modal-wrap')
    const elementToCheck = modal || window

    elementToCheck.addEventListener('scroll', handleScroll)
    return () => {
      elementToCheck.removeEventListener('scroll', handleScroll)
    }
  }, [])

  useEffect(() => {
    if (value && isValid(value)) {
      setSelectedTime(format(value, TIME_FORMAT.TWELVE_HOUR_FORMAT).toString())
    } else {
      setSelectedTime(null)
    }
  }, [value])

  const generateTimeSlots = () => {
    if (!options.length) {
      return []
    }
    const result: Date[] = []

    const interval = options[0]
    if (!interval.start_time || !interval.end_time) {
      return []
    }

    let current = new Date(interval.start_time)
    const end = new Date(interval.end_time)

    while (isBefore(current, end) || isEqual(current, end)) {
      result.push(new Date(current))
      current = addMinutes(current, 5)
    }

    return result
  }
  const timeSlots = generateTimeSlots()

  // Removes spaces and converts the time to 12 hour format if it is in 24 hour format from the custom input
  const formatTimeInput = (inputTime: string): string | null => {
    const cleanedTime = inputTime.replace(RegexService.removeSpaces(), '').toUpperCase()

    const format12HourTime = (match: RegExpExecArray) => {
      const hours = parseInt(match[1], 10)
      const minutes = match[2]
      const ampm = match[3]
      return `${hours}:${minutes} ${ampm}`
    }

    const format24HourTime = (match: RegExpExecArray) => {
      let hours = parseInt(match[1], 10)
      const minutes = match[2]
      const ampm = hours >= 12 ? 'PM' : 'AM'
      if (hours > 12) hours -= 12
      return `${hours}:${minutes} ${ampm}`
    }

    const twelveHourFormatMatch = RegexService.timeFormat12Hour().exec(cleanedTime)
    if (twelveHourFormatMatch) {
      return format12HourTime(twelveHourFormatMatch)
    }

    const twentyFourHourFormatMatch = RegexService.timeFormat24Hour().exec(cleanedTime)
    if (twentyFourHourFormatMatch) {
      return format24HourTime(twentyFourHourFormatMatch)
    }

    return null
  }

  const formattedTimeSlots = useMemo(
    () => timeSlots.map((timeSlot) => format(timeSlot, TIME_FORMAT.TWELVE_HOUR_FORMAT).toString()),
    [timeSlots],
  )

  const timeOptions = useCallback(() => {
    return formattedTimeSlots.map((time) => ({
      value: time,
      label: time,
    }))
  }, [formattedTimeSlots])

  const convertToISODate = (timeString: string): string => {
    if (typeof timeString !== 'string') {
      setFieldError && setFieldError(fieldName, INFO_CONSTANTS.INVALID_TIME)
      return
    }
    const [hoursPart, minutesPart, ampm] = timeString.split(RegexService.timeSplitter())
    let hours = parseInt(hoursPart, 10)
    const minutes = parseInt(minutesPart, 10)

    if (ampm === 'PM' && hours !== 12) {
      hours += 12
    } else if (ampm === 'AM' && hours === 12) {
      hours = 0
    }

    const dateTime = new Date() //@todo Fix the date/timezone here | old new Date(date) was returning the previous day

    dateTime.setHours(hours, minutes)

    return dateTime.toISOString()
  }

  const handleTimeChange = (value: string) => {
    if (typeof value !== 'string') {
      return
    }

    if (isValid(value) && !formattedTimeSlots.includes(value)) {
      formattedTimeSlots.push(value)
      formattedTimeSlots.sort()
    }
    setSelectedTime(value)
    const timeISO = convertToISODate(value)
    onUpdateField(new Date(timeISO))
  }

  const handleCustomTimeInput = (value: string) => {
    const formattedTime = formatTimeInput(value)
    if (formattedTime) {
      handleTimeChange(formattedTime)
    }
  }

  return (
    <div
      className={classNames(styles.parent, {
        [styles.parentIconContainerDisabled]: disabled,
      })}>
      <Select
        bordered={false}
        showAction={['click', 'focus']}
        showSearch
        placeholder={placeholder}
        className={styles.selectContainer}
        options={timeOptions()}
        onChange={handleTimeChange}
        onSearch={(value) => {
          handleCustomTimeInput(value)
          setCustomTime(value)
        }}
        disabled={disabled}
        value={selectedTime}
        open={isOpen}
        onOpenChange={(isOpen: boolean) => {
          setIsOpen(isOpen)
          isOpen ? onFocus?.() : onHide?.()
        }}
        notFoundContent={
          <div
            style={{
              color: '#272322',
              fontWeight: 500,
              display: 'flex',
              justifyContent: 'flex-start',
              alignItems: 'center',
              height: '100%',
            }}>
            {customTime}
          </div>
        }
        data-cy={`${fieldName}-time`}
      />
    </div>
  )
}
